import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { fetchUsers } from "../../reduxSlices/userSlice";
import schualedIcon from "../../assets/icons/schualed-icon.svg";
import cancelIcon from "../../assets/icons/cancel-icon.png";
import MobileNav from "../../components/mobileNav/MobileNav";
import "./techStatics.css";

const TechStatics = () => {
  const [stats, setStats] = useState({
    CompletedRequests: 0,
    CancelledRequests: 0,
    InProgressRequests: 0,
    TotalRequests: 0,
  });

  const [requests, setRequests] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);

  const [cachedRequests, setCachedRequests] = useState({});

  const params = useParams();
  const technicianId = params.techStaticsId;

  // Get user data from Redux - with proper ID matching
  const userData = useSelector((state) => {
    return state.users.list.find(
      (user) => user.Id === technicianId || user.id === technicianId
    );
  });

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  // Safely get maintenance area IDs from localStorage
  const getMaintenanceAreaIds = () => {
    try {
      const maintenanceAreaIdsString =
        localStorage.getItem("MaintenanceAreaIds") || "";
      return maintenanceAreaIdsString
        .split(",")
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !isNaN(id));
    } catch (e) {
      console.error("Error reading maintenance areas:", e);
      return [];
    }
  };

  const maintenanceAreaIds = getMaintenanceAreaIds();

  // Fetch data from API endpoint without pagination - recommended approach
  const fetchRequestData = async (url, payload) => {
    try {
      // First, get the total count
      const countPayload = {
        ...payload,
        pageNumber: 1,
        pageSize: 1,
      };

      const countResponse = await axios.post(url, countPayload);

      if (countResponse.data && Array.isArray(countResponse.data.Items)) {
        const totalItems = countResponse.data.TotalItems;

        // If no items or only a few, return from first call
        if (totalItems <= 50) {
          // Make a slightly larger request to get all items
          const itemsPayload = {
            ...payload,
            pageNumber: 1,
            pageSize: totalItems || 50,
          };

          const itemsResponse = await axios.post(url, itemsPayload);
          return itemsResponse.data?.Items || [];
        }

        // For larger datasets, use the total count
        const fullPayload = {
          ...payload,
          pageNumber: 1,
          pageSize: totalItems,
        };

        const fullResponse = await axios.post(url, fullPayload);
        return fullResponse.data?.Items || [];
      }

      return [];
    } catch (err) {
      console.error(`API Error (${url}):`, err);
      throw err;
    }
  };

  // Fetch counts and data for all request types
  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!maintenanceAreaIds.length) {
        throw new Error("No valid maintenance areas found");
      }

      // Fetch all data for each type
      const [completed, inProgress, cancelled] = await Promise.all([
        fetchRequestData(
          "https://api.generalhome.sa/api/maintenance/GetFinishedRequests",
          { maintenanceAreaIds, technicianId }
        ),
        fetchRequestData(
          "https://api.generalhome.sa/api/maintenance/GetInProgressRequests",
          { maintenanceAreaIds, technicianId }
        ),
        fetchRequestData(
          "https://api.generalhome.sa/api/maintenance/GetCancelRequests",
          { maintenanceAreaIds, technicianId }
        ),
      ]);

      setStats({
        CompletedRequests: completed.length,
        InProgressRequests: inProgress.length,
        CancelledRequests: cancelled.length,
        TotalRequests: completed.length + inProgress.length + cancelled.length,
      });

      // Cache the data
      setCachedRequests({
        completed: completed,
        inProgress: inProgress,
        cancelled: cancelled,
        all: [...completed, ...inProgress, ...cancelled],
      });

      // Set initial data to show all requests
      setRequests([...completed, ...inProgress, ...cancelled]);
      setActiveFilter("all");
    } catch (err) {
      console.error("Initial data error:", err);
      setError("فشل تحميل البيانات. يرجى المحاولة لاحقًا");
    } finally {
      setLoading(false);
    }
  };

  // Fetch initial data on component mount
  useEffect(() => {
    fetchAllData();
  }, [technicianId, maintenanceAreaIds.toString()]);

  // Filter requests based on active filter
  const filterRequests = (type) => {
    if (activeFilter === type) return;

    setActiveFilter(type);
    setTableLoading(true);

    // Use setTimeout to ensure UI updates smoothly
    setTimeout(() => {
      try {
        let filteredData = [];

        switch (type) {
          case "completed":
            filteredData = cachedRequests.completed || [];
            break;
          case "inProgress":
            filteredData = cachedRequests.inProgress || [];
            break;
          case "cancelled":
            filteredData = cachedRequests.cancelled || [];
            break;
          case "all":
            filteredData = cachedRequests.all || [];
            break;
          default:
            filteredData = [];
        }

        setRequests(filteredData);
      } catch (err) {
        console.error("Filter error:", err);
        setError("فشل تصفية البيانات");
      } finally {
        setTableLoading(false);
      }
    }, 100);
  };

  const getStatusClass = (status) => {
    if (status === "Completed" || status === "الطلبات التي تم تنفيذها") {
      return "status-completed";
    }
    if (status === "InProgress" || status === "المواعيد التي تم جدولتها") {
      return "status-in-progress";
    }
    if (status === "Cancelled" || status === "المواعيد التي تم إلغائها") {
      return "status-cancelled";
    }
    return "status-unknown";
  };

  if (loading) {
    return <div className="tech-statics-loading">جاري تحميل الإحصائيات...</div>;
  }

  if (error) {
    return <div className="tech-statics-error">{error}</div>;
  }

  return (
    <>
      <MobileNav />
      <div className="tech-statics-wrapper">
        {/* Stats Cards */}
        <div className="tech-statics-container">
          {/* User Info Card */}
          {userData && (
            <div className="user-info-card">
              <div className="user-info-header">
                <img
                  src={userData.Image}
                  alt={userData.FullName || "Technician"}
                />
              </div>
              <div className="user-info-body">
                <div className="user-info-row">
                  <span className="user-info-value">
                    {userData.FullName || "غير متوفر"}
                  </span>
                </div>
                <div className="user-info-row">
                  <p className="user-info-value">{userData.Email || ""}</p>
                </div>
              </div>
            </div>
          )}

          {/* Completed Requests Card */}
          <div
            className={`stat-card ${
              activeFilter === "completed" ? "active" : ""
            }`}
            onClick={() => filterRequests("completed")}
          >
            <div className="stat-card-header">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="25"
                height="24"
                viewBox="0 0 25 24"
                fill="none"
              >
                <path
                  d="M11.5476 21.5239H23.9286"
                  stroke="#16A34A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11.5476 12.9526H23.9286"
                  stroke="#16A34A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11.5476 4.38086H23.9286"
                  stroke="#16A34A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  opacity="0.6"
                  d="M1.07141 4.06361L2.49998 5.33345L6.7857 1.52393"
                  stroke="#16A34A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  opacity="0.6"
                  d="M1.07141 12.6349L2.49998 13.9047L6.7857 10.0952"
                  stroke="#16A34A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  opacity="0.6"
                  d="M1.07141 21.2067L2.49998 22.4765L6.7857 18.667"
                  stroke="#16A34A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <h3 className="doneReq">الطلبات المكتملة</h3>
            </div>
            <div className="stat-number">{stats.CompletedRequests}</div>
          </div>

          {/* In Progress Requests Card */}
          <div
            className={`stat-card ${
              activeFilter === "inProgress" ? "active" : ""
            }`}
            onClick={() => filterRequests("inProgress")}
          >
            <div className="stat-card-header">
              <img src={schualedIcon} alt="in progress" />
              <h3 className="schiueledReq">قيد التنفيذ</h3>
            </div>
            <div className="stat-number">{stats.InProgressRequests}</div>
          </div>

          {/* Cancelled Requests Card */}
          <div
            className={`stat-card ${
              activeFilter === "cancelled" ? "active" : ""
            }`}
            onClick={() => filterRequests("cancelled")}
          >
            <div className="stat-card-header">
              <img src={cancelIcon} alt="cancelled" />
              <h3 className="cancelReq">الطلبات الملغيه</h3>
            </div>
            <div className="stat-number">{stats.CancelledRequests}</div>
          </div>

          {/* Total Requests Card */}
          <div
            className={`stat-card ${activeFilter === "all" ? "active" : ""}`}
            onClick={() => filterRequests("all")}
          >
            <div className="stat-card-header">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
              >
                <path
                  d="M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16Z"
                  fill="#0D9AE1"
                  fillOpacity="0.16"
                />
                <path
                  d="M9.8378 16.7117L13.4949 19.977L22.0283 12.3579"
                  stroke="#0D9AE1"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <h3 className="totalReq">إجمالي الطلبات</h3>
            </div>
            <div className="stat-number">{stats.TotalRequests}</div>
          </div>
        </div>

        {/* Requests Table */}
        <div className="requests-table-container">
          {tableLoading ? (
            <div className="loading-table">جاري تحميل البيانات...</div>
          ) : (
            <div className="table-responsive-sm overflow-auto">
              <table className="requests-table table-striped">
                <thead>
                  <tr>
                    <th>رقم الطلب</th>
                    <th>اسم العميل</th>
                    <th>رقم الهاتف</th>
                    <th>المدينة</th>
                    <th>تاريخ الصيانة</th>
                    <th>الحالة</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.length > 0 ? (
                    requests.map((request, index) => {
                      const requestId = request.RequestId || `req-${index}`;
                      return (
                        <tr key={requestId}>
                          <td>
                            {request.FormattedRequestNumber ||
                              request.RequestId}
                          </td>
                          <td>{request.CustomerName || "غير متوفر"}</td>
                          <td>{request.PhoneNumber || "غير متوفر"}</td>
                          <td>{request.City || "غير متوفر"}</td>
                          <td>{request.MaintenanceStartDate || "غير محدد"}</td>
                          <td>
                            <span
                              className={`status-text ${
                                activeFilter === "all"
                                  ? getStatusClass(request.CurrentStatus)
                                  : activeFilter === "completed"
                                  ? "status-completed"
                                  : activeFilter === "inProgress"
                                  ? "status-in-progress"
                                  : "status-cancelled"
                              }`}
                            >
                              {activeFilter === "completed" && "مكتمل"}
                              {activeFilter === "inProgress" && "قيد التنفيذ"}
                              {activeFilter === "cancelled" && "ملغى"}
                              {activeFilter === "all" &&
                                (request.CurrentStatus === "Completed" ||
                                request.CurrentStatus ===
                                  "الطلبات التي تم تنفيذها"
                                  ? "مكتمل"
                                  : request.CurrentStatus === "InProgress" ||
                                    request.CurrentStatus ===
                                      "المواعيد التي تم جدولتها"
                                  ? "قيد التنفيذ"
                                  : request.CurrentStatus === "Cancelled" ||
                                    request.CurrentStatus ===
                                      "المواعيد التي تم إلغائها"
                                  ? "ملغى"
                                  : request.CurrentStatus || "غير محدد")}
                            </span>
                          </td>
                          <td>
                            {request.RequestId && (
                              <Link
                                to={`/dashboard/inProgress/${request.RequestId}`}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M9 18L15 12L9 6"
                                    stroke="#132239"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </Link>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="no-data">
                        لا توجد بيانات متاحة
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TechStatics;
